import { useEffect, useMemo, useState } from "react";
import Header from "../components/common/header";
import Footer from "../components/common/footer";
import { AlertTriangle, CheckCircle2, Search, Shield, XCircle } from "lucide-react";

const API_BASE = "https://cryptoswap.pro/token-risk-api";
const NETWORKS = ["ethereum", "base", "arbitrum", "optimism", "polygon", "bnb"];

function verdictClass(verdict) {
  return String(verdict || "UNKNOWN").toLowerCase();
}

function fmtUsd(value) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(Number(value || 0));
}

function fmtPct(value) {
  return Number.isFinite(Number(value)) ? `${Number(value)}%` : "-";
}

function fmtBool(value) {
  if (value === true) return "Yes";
  if (value === false) return "No";
  return "-";
}

function short(value) {
  if (!value) return "-";
  return value.length > 18 ? `${value.slice(0, 8)}...${value.slice(-6)}` : value;
}

function riskIcon(verdict) {
  const key = verdictClass(verdict);
  if (key === "safe") return <CheckCircle2 />;
  if (key === "blocked") return <XCircle />;
  return <AlertTriangle />;
}

export default function TokenRiskPage() {
  const [network, setNetwork] = useState("ethereum");
  const [address, setAddress] = useState("");
  const [result, setResult] = useState(null);
  const [latest, setLatest] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  async function loadLatest() {
    try {
      const response = await fetch(`/token-risk-latest.json?t=${Date.now()}`, {
        cache: "no-store",
      });
      if (!response.ok) return;
      const data = await response.json();
      setLatest(Array.isArray(data.items) ? data.items : []);
    } catch {
      // latest checks are optional
    }
  }

  useEffect(() => {
    loadLatest();
  }, []);

  const latestStats = useMemo(() => {
    const safe = latest.filter((item) => item.verdict === "SAFE").length;
    const warning = latest.filter((item) => item.verdict === "WARNING").length;
    const blocked = latest.filter((item) => item.verdict === "BLOCKED").length;
    return { safe, warning, blocked };
  }, [latest]);

  async function checkToken() {
    setLoading(true);
    setError("");
    setResult(null);

    try {
      const params = new URLSearchParams({
        net: network,
        address: address.trim(),
        force: "1",
      });
      const response = await fetch(`${API_BASE}/check?${params.toString()}`, {
        cache: "no-store",
      });
      if (!response.ok) throw new Error(`status ${response.status}`);
      const data = await response.json();
      setResult(data);
      loadLatest();
    } catch {
      setError("Token Risk service is not available right now.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="token-risk-page">
      <Header />
      <main className="token-risk-shell">
        <section className="token-risk-hero">
          <div>
            <div className="token-risk-kicker">
              <Shield size={16} />
              Token risk
            </div>
            <h1>Honeypot and scam check.</h1>
            <p>
              Check whether a token can be sold before bots, test wallets, or
              live execution are allowed to buy it.
            </p>
          </div>
          <div className="token-risk-status">
            <strong>Real buys stay blocked without a risk verdict.</strong>
            <span>GoPlus, honeypot checks, market liquidity, and route context.</span>
          </div>
        </section>

        <section className="token-risk-form">
          <label>
            Network
            <select value={network} onChange={(event) => setNetwork(event.target.value)}>
              {NETWORKS.map((item) => (
                <option value={item} key={item}>{item}</option>
              ))}
            </select>
          </label>
          <label>
            Token address
            <input
              value={address}
              onChange={(event) => setAddress(event.target.value)}
              placeholder="0x..."
            />
          </label>
          <button type="button" disabled={loading || !address.trim()} onClick={checkToken}>
            <Search size={18} />
            {loading ? "Checking..." : "Check Token"}
          </button>
        </section>

        {error && <div className="token-risk-error">{error}</div>}

        {result && (
          <section className={`token-risk-result ${verdictClass(result.verdict)}`}>
            <RiskMetric label="Verdict" value={result.verdict || "UNKNOWN"} />
            <RiskMetric label="Score" value={result.score ?? "-"} />
            <RiskMetric label="Sell tax" value={fmtPct(result.goplus?.sellTaxPct ?? result.honeypot?.sellTaxPct)} />
            <RiskMetric label="Buy tax" value={fmtPct(result.goplus?.buyTaxPct ?? result.honeypot?.buyTaxPct)} />
            <RiskMetric label="Liquidity" value={fmtUsd(result.market?.liquidityUsd)} />
            <RiskMetric label="Market" value={result.market?.dexId || "-"} />
            <RiskMetric label="Honeypot" value={fmtBool(result.goplus?.isHoneypot ?? result.honeypot?.isHoneypot)} />
            <RiskMetric label="Cannot sell all" value={fmtBool(result.goplus?.cannotSellAll)} />
            <RiskMetric label="Blacklist risk" value={fmtBool(result.goplus?.isBlacklisted)} />
            <RiskMetric label="Pausable" value={fmtBool(result.goplus?.transferPausable)} />
            <div className="token-risk-reasons">
              <span>Reasons</span>
              <strong>{(result.reasons || []).join(", ") || "-"}</strong>
            </div>
          </section>
        )}

        <section className="token-risk-summary">
          <RiskStat label="Safe" value={latestStats.safe} tone="safe" />
          <RiskStat label="Warning" value={latestStats.warning} tone="warning" />
          <RiskStat label="Blocked" value={latestStats.blocked} tone="blocked" />
          <RiskStat label="Latest checks" value={latest.length} />
        </section>

        <section className="token-risk-latest">
          <div className="token-risk-latest-head">
            <h2>Latest Checks</h2>
            <span>Updated by the token-risk service</span>
          </div>
          <div className="token-risk-list">
            {latest.slice(0, 40).map((item) => (
              <div className="token-risk-row" key={`${item.net}-${item.tokenAddress}-${item.checkedAt}`}>
                <span className={`token-risk-badge ${verdictClass(item.verdict)}`}>
                  {riskIcon(item.verdict)}
                  {item.verdict || "UNKNOWN"}
                </span>
                <strong>{item.market?.baseSymbol || short(item.tokenAddress)}</strong>
                <em>
                  {item.net} | sell tax {fmtPct(item.goplus?.sellTaxPct ?? item.honeypot?.sellTaxPct)} | {fmtUsd(item.market?.liquidityUsd)}
                </em>
              </div>
            ))}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

function RiskMetric({ label, value }) {
  return (
    <div className="token-risk-metric">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function RiskStat({ label, value, tone = "" }) {
  return (
    <div className={`token-risk-stat ${tone}`}>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}
